import { Location } from '@angular/common';
import type { ComponentRef, Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { Subscriber } from 'rxjs';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  fromEventPattern,
  merge,
  Observable,
  Subject,
  take,
  tap,
} from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { GlobalProductUpdatesState } from '@core/product-updates-legacy';
import { WalkthroughRegisterContract } from '@core/walkthrough-contracts';
import type { WalkthroughStepOrigin } from '@core/walkthrough-types';

import type { RenderRef, Step, WalkthroughComponentT, WalkthroughInputs } from '../types';
import { WalkthroughState } from '../types';
import { WalkthroughComponentRenderer } from './walkthrough-component.renderer';

@Injectable({ providedIn: 'root' })
export class WalkthroughStepsService implements WalkthroughRegisterContract {
  private readonly componentRenderer = inject(WalkthroughComponentRenderer);
  private readonly location = inject(Location);

  public activeStep$: Observable<Step | null> = new Observable<Step | null>();
  public activeStep: Signal<Step | null | undefined>;
  public isWalkthroughInProgress$: Observable<boolean> = new Observable<boolean>();
  public isWalkthroughInProgress: Signal<boolean | undefined>;
  public onLastStep$: Observable<void>;

  private activeStepSubject$: BehaviorSubject<Step | null> = new BehaviorSubject<Step | null>(null);

  private registeredStepsMap$: BehaviorSubject<Map<Step['id'], WalkthroughStepOrigin>> =
    new BehaviorSubject(new Map());

  private teardown$: Subject<void> | null;
  private _onLastStep$: Subject<void> = new Subject<void>();

  private globalProductUpdatesState = inject(GlobalProductUpdatesState);

  constructor() {
    this.onLastStep$ = this._onLastStep$;

    this.activeStep$ = this.activeStepSubject$;

    this.activeStep = toSignal<Step | null>(this.activeStepSubject$, {
      initialValue: null,
    });

    this.isWalkthroughInProgress$ = this.activeStepSubject$.pipe(
      map(activeStep => !!activeStep),
      distinctUntilChanged(),
    );
    this.isWalkthroughInProgress = toSignal(this.isWalkthroughInProgress$, {
      initialValue: false,
    });
  }

  public start(configuration: Step[]): Observable<WalkthroughState> {
    this.teardownFlow();
    this.globalProductUpdatesState.toggleEnabled(false);

    return new Observable<WalkthroughState>(walkthroughState => {
      const teardown$ = new Subject<void>();
      const source$ = combineLatest([this.activeStepSubject$, this.registeredStepsMap$]);
      this.teardown$ = teardown$;

      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntil(this.teardown$))
        .subscribe(event => event.preventDefault());

      source$
        .pipe(
          filter(
            ([activeStep, registeredStepsMap]) =>
              !!activeStep && registeredStepsMap.has(activeStep!.id),
          ),
          distinctUntilChanged((prev, curr) => prev[0]?.id === curr[0]?.id),
          takeUntil(teardown$),
        )
        .subscribe(([activeStep, registeredStepsMap]) => {
          const activeRegisteredStepDirective = registeredStepsMap.get(activeStep!.id)!;
          const renderRef = this.renderStepComponent(activeRegisteredStepDirective, activeStep!);

          this.setComponentInputs(renderRef.componentRef, activeStep!, configuration.length);

          this.handleWalkthroughComponentEvents(
            activeRegisteredStepDirective,
            renderRef,
            teardown$,
            walkthroughState,
            configuration,
          );

          this.handleLocationEvents();
        });

      this.activeStepSubject$.next(configuration[0]);
      walkthroughState.next(WalkthroughState.Active);
    });
  }

  public register(id: Step['id'], originDirective: WalkthroughStepOrigin): void {
    const currentMap = this.registeredStepsMap$.value;
    currentMap.set(id, originDirective);
    this.registeredStepsMap$.next(new Map(currentMap));
  }

  public unregister(id: Step['id']): void {
    const currentMap = this.registeredStepsMap$.value;
    currentMap.delete(id);
    this.registeredStepsMap$.next(new Map(currentMap));
  }

  public updateOverlayPositions() {
    window.dispatchEvent(new Event('resize'));
  }

  private getNextStep(configuration: Step[], currentStep: number): Step {
    return configuration[currentStep];
  }

  private renderStepComponent(originDirective: WalkthroughStepOrigin, activeStep: Step): RenderRef {
    const componentDef = activeStep.component.definition;
    const renderOptions = activeStep.renderOptions;

    return {
      ...this.componentRenderer.render(componentDef, renderOptions, originDirective),
      originDirective,
    };
  }

  private setComponentInputs(
    componentRef: ComponentRef<WalkthroughComponentT>,
    activeStep: Step,
    stepsTotal: number,
  ): void {
    componentRef.setInput('stepsTotal', stepsTotal);
    componentRef.setInput('currentStep', activeStep.position);
    this.setActiveStepInputs(activeStep, componentRef);

    componentRef.changeDetectorRef.detectChanges();
  }

  private setActiveStepInputs(activeStep: Step, componentRef: ComponentRef<WalkthroughComponentT>) {
    Object.keys(activeStep.component.inputs).forEach((key: keyof WalkthroughInputs) => {
      componentRef.setInput(key, activeStep.component.inputs[key]);
    });
  }

  private handleWalkthroughComponentEvents(
    stepOriginDirective: WalkthroughStepOrigin,
    renderRef: RenderRef,
    teardown$: Subject<void>,
    walkthroughState: Subscriber<WalkthroughState>,
    configuration: Step[],
  ): void {
    const { componentRef, originDirective } = renderRef;

    const { next, cancel, finish, currentStep } = componentRef.instance;

    cancel.pipe(take(1), takeUntil(teardown$)).subscribe(() => {
      this.componentRenderer.dispose(originDirective.cleanupCallback());
    });

    finish.pipe(take(1), takeUntil(teardown$)).subscribe(() => {
      this.componentRenderer.dispose(originDirective.afterDisposeCallback());

      const currentStepConfig = configuration[currentStep() - 1];
      if (currentStepConfig?.finishHandler) currentStepConfig.finishHandler();
    });

    merge(
      cancel.pipe(tap(() => walkthroughState.next(WalkthroughState.Canceled))),
      finish.pipe(tap(() => walkthroughState.next(WalkthroughState.Finished))),
    )
      .pipe(take(1), takeUntil(teardown$))
      .subscribe(() => {
        this.teardownFlow();
      });

    next.pipe(take(1), takeUntil(teardown$)).subscribe(args => {
      const step = configuration[currentStep()];
      if (step?.afterDisposeCallback) step.afterDisposeCallback();
    });

    next.pipe(take(1), takeUntil(teardown$)).subscribe(args => {
      const nextStep = this.getNextStep(configuration, currentStep());
      if (nextStep?.beforeRenderCallback) nextStep.beforeRenderCallback();
    });

    next.pipe(take(1), takeUntil(teardown$)).subscribe(args => {
      this.componentRenderer.dispose(originDirective.afterDisposeCallback(), <any>args);
      const nextStep = this.getNextStep(configuration, currentStep());

      if (nextStep?.position === configuration.length) this._onLastStep$.next();

      nextStep ? this.activeStepSubject$.next(nextStep) : finish.next();
    });
  }

  private teardownFlow(): void {
    if (!this.teardown$) return;
    this.componentRenderer.dispose();
    this.activeStepSubject$.next(null);
    this.globalProductUpdatesState.toggleEnabled(true);
    this.teardown$.next();
    this.teardown$.complete();
    this.teardown$ = null;
  }

  private handleLocationEvents() {
    const location$ = fromEventPattern(handler => this.location.subscribe(handler));
    location$.pipe(take(1)).subscribe(() => {
      this.teardownFlow();
    });
  }
}
